import React, { useMemo, useState } from 'react';
import { Checkbox, CircularProgress, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ExpandMore } from '@mui/icons-material';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import { SettingsCard } from '../../components/composed';
import { Box, Typography } from '../../components/primitives';
import { formatDateRelative, formatTime } from '../../utils/formatters';
import CallToAction from '../DataBuilderPage/CallToAction';

const columnHelper = createColumnHelper();

const columns = [
  columnHelper.display({
    id: 'select',
    enableSorting: false,
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllRowsSelected()}
        indeterminate={table.getIsSomeRowsSelected()}
        onChange={table.getToggleAllRowsSelectedHandler()}
        onClick={(e) => e.stopPropagation()}
        inputProps={{ 'aria-label': 'Select all events' }}
        size="small"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect()}
        onChange={row.getToggleSelectedHandler()}
        onClick={(e) => e.stopPropagation()}
        inputProps={{ 'aria-label': `Select event ${row.original.id}` }}
        size="small"
      />
    ),
  }),
  columnHelper.accessor((row) => row.start_time ?? '', {
    id: 'date',
    header: 'Date',
    sortingFn: 'datetime',
    cell: (info) => formatDateRelative(info.getValue()) || 'N/A',
  }),
  columnHelper.accessor((row) => row.start_time ?? '', {
    id: 'start_time',
    header: 'Start Time',
    sortingFn: 'datetime',
    cell: (info) => formatTime(info.getValue()) || 'N/A',
  }),
  columnHelper.accessor((row) => row.end_time ?? '', {
    id: 'end_time',
    header: 'End Time',
    sortingFn: 'datetime',
    cell: (info) => formatTime(info.getValue()) || 'N/A',
  }),
  columnHelper.accessor((row) => row.facility?.address?.city ?? '', {
    id: 'city',
    header: 'City',
    sortingFn: 'alphanumeric',
    cell: (info) => info.getValue() || 'N/A',
  }),
  columnHelper.accessor((row) => row.facility?.name ?? '', {
    id: 'facility_name',
    header: 'Facility Name',
    sortingFn: 'alphanumeric',
    cell: (info) => info.getValue() || 'N/A',
  }),
  columnHelper.accessor((row) => row.resourceDetail?.name ?? '', {
    id: 'resource_name',
    header: 'Resource Name',
    sortingFn: 'alphanumeric',
    cell: (info) => info.getValue() || 'N/A',
  }),
  columnHelper.accessor((row) => row.name ?? '', {
    id: 'event_name',
    header: 'Event Name',
    sortingFn: 'alphanumeric',
    cell: (info) => info.getValue() || 'N/A',
  }),
];

const cardSx = {
  mb: 0,
  margin: 0,
  borderRadius: 0,
  padding: 0,
  '& > div:first-of-type': { display: 'none' },
  '& > hr': { display: 'none' },
};

const EventsList = ({
  events,
  resources,
  facilities,
  loading,
  error,
  expanded = true,
  onTitleClick,
}) => {
  const theme = useTheme();
  const [sorting, setSorting] = useState([{ id: 'date', desc: false }]);
  const [rowSelection, setRowSelection] = useState({});

  const enrichedEvents = useMemo(() => {
    if (!events || !resources || !facilities) return [];

    const resourceMap = new Map(resources.map((r) => [r.id, r]));
    const facilityMap = new Map(facilities.map((f) => [f.id, f]));

    return events.map((event) => {
      const resourceDetail = resourceMap.get(event.resource);
      const facility = resourceDetail
        ? facilityMap.get(resourceDetail.facility)
        : null;

      return {
        ...event,
        resourceDetail,
        facility,
      };
    });
  }, [events, resources, facilities]);

  const table = useReactTable({
    data: enrichedEvents,
    columns,
    state: { sorting, rowSelection },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    getRowId: (row) => String(row.id),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const selectedEventIds = table
    .getSelectedRowModel()
    .rows.map((row) => row.original.id);

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </Box>
      );
    }

    if (enrichedEvents.length === 0) {
      return (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No events found.
            </Typography>
          </Box>
          <CallToAction />
        </>
      );
    }

    return (
      <>
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const canSort = header.column.getCanSort();
                    const sortDirection = header.column.getIsSorted();
                    return (
                      <th
                        key={header.id}
                        onClick={
                          canSort
                            ? header.column.getToggleSortingHandler()
                            : undefined
                        }
                        style={{
                          textAlign: 'left',
                          paddingRight: 10,
                          borderBottom: `1px solid ${theme.palette.primary.main}`,
                          ...(canSort ? { cursor: 'pointer' } : {}),
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {sortDirection === 'asc'
                          ? ' \u25B2'
                          : sortDirection === 'desc'
                          ? ' \u25BC'
                          : ''}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={{
                        textAlign: 'left',
                        paddingRight: 10,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
        <CallToAction />
      </>
    );
  };

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 -50px 50px rgba(29, 43, 58, 0.12)',
      }}
    >
      <Box
        onClick={onTitleClick}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          px: 2,
          pt: 2,
          pb: 1.5,
          flexShrink: 0,
          cursor: onTitleClick ? 'pointer' : 'default',
          userSelect: 'none',
        }}
      >
        <Box>
          <Typography variant="h6">Results</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {loading
              ? 'Loading results…'
              : `${enrichedEvents.length} result${
                  enrichedEvents.length === 1 ? '' : 's'
                } found...`}
          </Typography>
        </Box>
        {onTitleClick && (
          <IconButton
            size="small"
            aria-label={expanded ? 'Collapse results' : 'Expand results'}
            aria-expanded={expanded}
            onClick={(e) => {
              e.stopPropagation();
              onTitleClick();
            }}
          >
            <ExpandMore
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            />
          </IconButton>
        )}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', width: '100%' }}>
        <SettingsCard title={null} sx={cardSx}>
          {renderContent()}
        </SettingsCard>
      </Box>
    </Box>
  );
};

export default EventsList;
